import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  AsyncSelect,
  CheckForm,
  Form,
  Group,
  Grid,
  Input,
  Modal,
  PositiveNumberInput,
  Select,
  Label,
  Typography,
  Flex,
} from "@samskip/frontend-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { LabelConstants, DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { CommonTypes, DomesticTypes } from "@assets/types";
import {
  hazardousMaterialLabelMapper,
  hazardousMaterialValueMapper,
  itemConditionLabelMapper,
  itemConditionValueMapper,
} from "@assets/utilities/labelMapperUtils";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import { DomesticYup } from "@assets/yupObjects";
import { WithFormController } from "@components";

type CargoDetailModalProps = {
  isChristmasPackageChecked: boolean;
  itemConditions: DomesticTypes.DomesticItemCondition[];
  onModalClose: CommonTypes.CallbackFunction;
  onSave: (data: DomesticFormTypes.CargoDetailForm, index?: number) => void;
  packageDetail?: DomesticFormTypes.CargoDetailForm;
  searchHazardousMaterial: (
    searchString: string
  ) => Promise<DomesticTypes.DomesticHazardousMaterial[]>;
};

const CargoDetailModal: React.FC<CargoDetailModalProps> = ({
  isChristmasPackageChecked,
  itemConditions,
  onModalClose,
  onSave,
  packageDetail = DomesticFormTypes.defaultCargoDetailForm,
  searchHazardousMaterial,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    defaultValues: packageDetail,
    mode: "onBlur",
    resolver: yupResolver(
      DomesticYup.cargoDetailValidationSchema(isChristmasPackageChecked)
    ),
  });

  const handleSave = (data: DomesticFormTypes.CargoDetailForm) => {
    onSave(data);
  };

  const resetHazardousMaterialSelection = (
    oldHazardousValue: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Reset HazardousMaterials class if the hazard toggle is unchecked
    if (!oldHazardousValue.target.checked) {
      setValue(
        "HazardousMaterial",
        DomesticFormTypes.defaultCargoDetailForm.HazardousMaterial
      );
    }
  };

  return (
    <Modal
      closeIcon
      onClose={onModalClose}
      open
      primaryButtonProps={{
        children: t("LABEL_SAVE"),
        onClick: handleSubmit(handleSave),
        form: "details_form",
      }}
      secondaryButtonProps={{
        children: t("LABEL_CANCEL"),
        onClick: onModalClose,
      }}
      size="medium"
      title={t("LABEL_PACKAGE_DETAILS")}
    >
      <Form gap="small" id="details_form" onSubmit={handleSubmit(handleSave)}>
        <Input
          errorMessage={getErrorMessage("Description", errors, {
            maxVal:
              DomesticConstants.INPUT_CONSTRAINTS.MAX_DETAIL_DESCRIPTION_LENGTH,
          })}
          fullWidth
          hasError={errors.Description != null}
          id="cargo-description"
          label={t("LABEL_CARGODESCRIPTION")}
          maxLength={
            DomesticConstants.INPUT_CONSTRAINTS.MAX_DETAIL_DESCRIPTION_LENGTH
          }
          placeholder={t("PLACEHOLDER_CARGO_DESCRIPTION")}
          required
          {...register("Description")}
        />
        <WithFormController control={control} name="Type">
          <Select
            errorMessage={getErrorMessage("Type", errors)}
            fullWidth
            getOptionLabel={itemConditionLabelMapper}
            getOptionValue={itemConditionValueMapper}
            hasError={errors.Type != null}
            id="cargo-type"
            label={t("LABEL_CARGOTYPE")}
            options={itemConditions}
            placeholder={t("PLACEHOLDER_SELECT_TYPE")}
            required
          />
        </WithFormController>

        <Grid mdCol={2}>
          <PositiveNumberInput
            errorMessage={getErrorMessage("Quantity", errors)}
            fullWidth
            hasError={errors.Quantity != null}
            id="cargo-quantity"
            label={`${t("TEXT_QUANTITY")} (PCS)`}
            placeholder={t("PLACEHOLDER_QUANTITY")}
            required
            {...register("Quantity")}
          />
          {!isChristmasPackageChecked && (
            <Input
              errorMessage={getErrorMessage("Weight", errors)}
              hasError={errors.Weight != null}
              id="cargo-weight"
              label={`${t("LABEL_GROSS_WEIGHT")} (KG)`}
              placeholder={t("PLACEHOLDER_GROSS_WEIGHT")}
              type="number"
              {...register("Weight")}
            />
          )}
        </Grid>
        {isChristmasPackageChecked && (
          <Flex gap="xsmall" vertical>
            <Label labelCase="upper" required>
              {`${t("LABEL_GROSS_WEIGHT")} (KG)`}
            </Label>
            <Flex gap="small" vertical>
              <Typography variant="info">
                {t("INFO_CHRISTMAS_PACKAGE_WEIGHT_LIMIT")}
              </Typography>
              <CheckForm
                errorMessage={getErrorMessage("WeightConfirmation", errors)}
                hasError={errors.WeightConfirmation != null}
                id="weight-confirmation"
                label={t("LABEL_CHRISTMAS_PACKAGE_WEIGHT_CONFIRMATION")}
                {...register("WeightConfirmation")}
              />
            </Flex>
          </Flex>
        )}
        <Group title={t("LABEL_SPECIAL_HANDLING")}>
          {!isChristmasPackageChecked && (
            <CheckForm
              id="hazardous"
              label={t("LABEL_HAZARDOUS_MATERIALS")}
              {...register("Hazardous", {
                onChange: resetHazardousMaterialSelection,
              })}
            />
          )}
          <CheckForm
            id="fragile"
            label={t("LABEL_FRAGILE")}
            {...register("Fragile")}
          />
        </Group>
        {watch("Hazardous") && (
          <WithFormController control={control} name="HazardousMaterial">
            <AsyncSelect
              defaultOptionsMessage={t(
                LabelConstants.SELECT_LABELS.DEFAULT_OPTIONS_MESSAGE
              )}
              errorMessage={getErrorMessage("HazardousMaterial", errors)}
              fullWidth
              getOptionLabel={hazardousMaterialLabelMapper}
              getOptionValue={hazardousMaterialValueMapper}
              hasError={errors.HazardousMaterial != null}
              id="cargo-hazardous-material"
              label={t("LABEL_HAZARDCLASS")}
              loadOptions={searchHazardousMaterial}
              noOptionsMessage={t(LabelConstants.SELECT_LABELS.NO_OPTIONS)}
              placeholder={t("PLACEHOLDER_SEARCH_HAZARDOUS_MATERIAL")}
              portaling
              required
            />
          </WithFormController>
        )}
      </Form>
    </Modal>
  );
};

export default CargoDetailModal;
